import type {
  AvailabilityStatus,
  OrderStatus,
  PartFigures,
  Readiness,
  UnitCode,
  WorkOrderStatus,
} from "@bt/domain";
import {
  AlertCircleIcon,
  ArchiveIcon,
  CheckCircle2Icon,
  CircleDashedIcon,
  CircleDotIcon,
  CircleIcon,
  ClockIcon,
  LightbulbIcon,
  PackageCheckIcon,
  PackageIcon,
  PauseCircleIcon,
  TruckIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useFormat } from "@/lib/format";

const AVAILABILITY = {
  in_stock: { tone: "ok", icon: CheckCircle2Icon },
  on_order: { tone: "transit", icon: TruckIcon },
  partial: { tone: "partial", icon: CircleDotIcon },
  missing: { tone: "missing", icon: AlertCircleIcon },
} as const;

export function AvailabilityChip({
  status,
  tool = false,
}: {
  status: AvailabilityStatus;
  tool?: boolean;
}) {
  const { t } = useTranslation();
  const { tone, icon: Icon } = AVAILABILITY[status];
  return (
    <Badge tone={tone}>
      {tool ? <WrenchIcon /> : <Icon />}
      {t(`availability.${status}`)}
    </Badge>
  );
}

const READINESS = {
  ready: { tone: "ok", icon: CheckCircle2Icon },
  waiting: { tone: "transit", icon: TruckIcon },
  missing: { tone: "missing", icon: AlertCircleIcon },
} as const;

export function ReadinessChip({ readiness }: { readiness: Readiness }) {
  const { t } = useTranslation();
  const { tone, icon: Icon } = READINESS[readiness];
  return (
    <Badge tone={tone}>
      <Icon />
      {t(`readiness.${readiness}`)}
    </Badge>
  );
}

const WORK_ORDER_STATUS = {
  idea: { tone: "neutral", icon: LightbulbIcon },
  planned: { tone: "outline", icon: CircleIcon },
  in_progress: { tone: "brand", icon: CircleDashedIcon },
  completed: { tone: "ok", icon: CheckCircle2Icon },
  cancelled: { tone: "neutral", icon: XCircleIcon },
} as const;

export function WorkOrderStatusBadge({ status }: { status: WorkOrderStatus }) {
  const { t } = useTranslation();
  const { tone, icon: Icon } = WORK_ORDER_STATUS[status];
  return (
    <Badge tone={tone}>
      <Icon />
      {t(`workOrderStatus.${status}`)}
    </Badge>
  );
}

const ORDER_STATUS = {
  draft: { tone: "neutral", icon: PauseCircleIcon },
  ordered: { tone: "transit", icon: TruckIcon },
  partially_received: { tone: "partial", icon: PackageIcon },
  received: { tone: "ok", icon: PackageCheckIcon },
  cancelled: { tone: "neutral", icon: XCircleIcon },
} as const;

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const { t } = useTranslation();
  const { tone, icon: Icon } = ORDER_STATUS[status];
  return (
    <Badge tone={tone}>
      <Icon />
      {t(`orderStatus.${status}`)}
    </Badge>
  );
}

/** The single most useful stock signal for a part in lists. */
export function StockStatusChip({
  figures,
  unit,
  archived,
}: {
  figures: PartFigures;
  unit: UnitCode;
  archived?: boolean;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  if (archived) {
    return (
      <Badge tone="neutral">
        <ArchiveIcon />
        {t("common.archived")}
      </Badge>
    );
  }
  if (figures.toOrder > 0) {
    return (
      <Badge tone="missing">
        <AlertCircleIcon />
        {t("stockStatus.toOrder", { qty: format.qty(figures.toOrder, unit) })}
      </Badge>
    );
  }
  if (figures.onOrder > 0) {
    return (
      <Badge tone="transit">
        <TruckIcon />
        {t("stockStatus.onOrder")}
      </Badge>
    );
  }
  if (figures.belowMinimum) {
    return (
      <Badge tone="partial">
        <ClockIcon />
        {t("stockStatus.low")}
      </Badge>
    );
  }
  if (figures.onHand > 0) {
    return (
      <Badge tone="ok">
        <CheckCircle2Icon />
        {t("stockStatus.inStock")}
      </Badge>
    );
  }
  return (
    <Badge tone="outline">
      <CircleIcon />
      {t("stockStatus.outOfStock")}
    </Badge>
  );
}
